import React, { useState } from 'react';
import API from '../../Services/api';

const CATEGORIES = [
  { value: 'bug', label: '🐛 Bug Report', color: 'bg-red-50 border-red-200 text-red-700' },
  { value: 'feature', label: '✨ Feature Request', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  { value: 'general', label: '💬 General Feedback', color: 'bg-gray-50 border-gray-200 text-gray-700' },
  { value: 'compliment', label: '🌟 Compliment', color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
];

function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) { setError('Please select a category.'); return; }
    if (message.trim().length < 5) { setError('Please write at least 5 characters.'); return; }
    setError('');
    setSubmitting(true);
    try {
      await API.post('/feedback', { category, message });
      setSuccess(true);
      setMessage('');
      setCategory('');
      setTimeout(() => {
        setSuccess(false);
        setOpen(false);
      }, 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9990]">
      {open && (
        <div className="mb-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-[fadeIn_0.2s_ease]">
          <div className="bg-gradient-to-r from-[#29752B] to-[#34A853] px-4 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold text-sm">Share Feedback</h3>
              <p className="text-green-100 text-xs mt-0.5">Help us improve e-save</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white text-xl leading-none">×</button>
          </div>

          {success ? (
            <div className="px-5 py-8 text-center">
              <div className="text-4xl mb-2">🎉</div>
              <p className="text-[#29752B] font-semibold">Thank you for your feedback!</p>
              <p className="text-gray-500 text-sm mt-1">We really appreciate it.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-2">What kind of feedback?</p>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setCategory(cat.value)}
                      className={`border rounded-xl px-2 py-2 text-xs font-medium text-left transition-all ${
                        category === cat.value
                          ? cat.color + ' ring-2 ring-offset-1 ring-[#29752B]'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us more…"
                  rows={3}
                  maxLength={1000}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#29752B] resize-none"
                />
                <p className="text-right text-xs text-gray-400">{message.length}/1000</p>
              </div>

              {error && <p className="text-red-500 text-xs">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#29752B] text-white rounded-xl py-2.5 text-sm font-medium hover:bg-[#1e5c20] disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Sending…' : 'Send Feedback'}
              </button>
            </form>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 bg-[#29752B] text-white px-4 py-3 rounded-full shadow-lg hover:bg-[#1e5c20] transition-all hover:scale-105 active:scale-95"
        title="Share feedback"
      >
        <span className="text-lg">💬</span>
        <span className="text-sm font-medium">{open ? 'Close' : 'Feedback'}</span>
      </button>
    </div>
  );
}

export default FeedbackWidget;
